import type { Key, KeyboardEvent as ReactKeyboardEvent, ReactNode, Ref } from 'react';
import { useMemo } from 'react';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { Autocomplete, Input, ListBox, ListBoxItem, SearchField, Text } from '@launchpad-ui/components';
import { Icon, type IconName } from '@launchpad-ui/icons';
import clsx from 'clsx';

import styles from './FilterValueAutocomplete.module.css';

export type FilterValueOption = {
  value: string | number | boolean;
  displayName?: string;
  resultsCount?: number;
  description?: string;
  iconName?: IconName;
  disabled?: boolean;
  typeLabel?: string;
};

export interface FilterValueAutocompleteProps {
  options: FilterValueOption[];
  inputValue: string;
  onInputChange: (value: string) => void;
  onSelect: (value: string | number | boolean) => void;
  isLoading?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  className?: string;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  autoFocus?: boolean;
  inputRef?: Ref<HTMLInputElement>;
  id?: string;
  multiSelect?: boolean;
  selectedValues?: Array<string | number | boolean>;
  renderOption?: (option: FilterValueOption) => ReactNode;
  showSearchField?: boolean;
  onDismiss?: () => void;
}

function optionId(option: FilterValueOption): string {
  return String(option.value);
}

function optionLabel(option: FilterValueOption): string {
  return option.displayName ?? String(option.value);
}

const filterByText = (textValue: string, inputValue: string) =>
  textValue.toLowerCase().includes(inputValue.trim().toLowerCase());

function defaultOptionContent(option: FilterValueOption): ReactNode {
  return (
    <>
      <span className={styles.label}>
        {option.iconName && <Icon name={option.iconName} size="small" />}
        <Text slot="label">{optionLabel(option)}</Text>
        {option.typeLabel && <span className={styles.typeLabel}>{option.typeLabel}</span>}
      </span>
      {option.description ? <Text slot="description">{option.description}</Text> : null}
      {option.resultsCount !== undefined && (
        <span className={styles.resultsCount}>{option.resultsCount.toLocaleString()}</span>
      )}
    </>
  );
}

export function FilterValueAutocomplete({
  options,
  inputValue,
  onInputChange,
  onSelect,
  isLoading = false,
  placeholder,
  'aria-label': ariaLabel = msg('queryBuilder.filterValues.filterValueLabel'),
  className,
  isDisabled,
  isReadOnly,
  autoFocus,
  inputRef,
  id,
  multiSelect = false,
  selectedValues,
  renderOption,
  showSearchField = true,
  onDismiss,
}: FilterValueAutocompleteProps) {
  const selectedKeys = useMemo(() => new Set((selectedValues ?? []).map(String)), [selectedValues]);

  const dedupedOptions = useMemo(() => {
    const seen = new Set<string>();
    return options.filter((option) => {
      const key = String(option.value);
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [options]);

  const handleEscapeCapture = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape' && onDismiss) {
      e.preventDefault();
      e.stopPropagation();
      onDismiss();
    }
  };

  const emptyState = (
    <div className={styles.empty}>
      {isLoading ? (
        <Message id="queryBuilder.filterValues.loading" />
      ) : (
        <Message id="queryBuilder.filterValues.noOptions" />
      )}
    </div>
  );

  const handleSelectionChange = (keys: 'all' | Set<Key>) => {
    if (keys === 'all') {
      return;
    }
    const next = new Set(Array.from(keys, String));

    let changedKey: string | undefined;
    if (multiSelect) {
      const toggled: string[] = [];
      for (const key of next) {
        if (!selectedKeys.has(key)) {
          toggled.push(key);
        }
      }
      for (const key of selectedKeys) {
        if (!next.has(key)) {
          toggled.push(key);
        }
      }
      if (toggled.length !== 1) {
        return;
      }
      [changedKey] = toggled;
    } else {
      [changedKey] = next;
    }

    if (changedKey === undefined) {
      return;
    }
    const match = dedupedOptions.find((option) => optionId(option) === changedKey);
    if (match) {
      onSelect(match.value);
    }
  };

  const listBoxContent = (option: FilterValueOption) => (
    <ListBoxItem
      id={optionId(option)}
      textValue={optionLabel(option)}
      isDisabled={option.disabled}
      className={styles.item}
    >
      {renderOption ? renderOption(option) : defaultOptionContent(option)}
    </ListBoxItem>
  );

  const list = (
    <ListBox<FilterValueOption>
      id={id}
      aria-label={ariaLabel}
      items={dedupedOptions}
      className={styles.listBox}
      selectionMode={multiSelect ? 'multiple' : 'single'}
      selectedKeys={selectedKeys}
      disallowEmptySelection={!multiSelect}
      onSelectionChange={handleSelectionChange}
      renderEmptyState={() => emptyState}
      autoFocus={showSearchField ? undefined : autoFocus}
    >
      {listBoxContent}
    </ListBox>
  );

  if (!showSearchField) {
    return (
      <div className={clsx(styles.root, className)} onKeyDownCapture={handleEscapeCapture}>
        {list}
      </div>
    );
  }

  return (
    <Autocomplete filter={filterByText} inputValue={inputValue} onInputChange={onInputChange}>
      <div className={clsx(styles.root, className)} onKeyDownCapture={handleEscapeCapture}>
        <SearchField
          aria-label={ariaLabel}
          className={styles.search}
          autoFocus={autoFocus}
          isDisabled={isDisabled}
          isReadOnly={isReadOnly}
        >
          <Input ref={inputRef} placeholder={placeholder} />
        </SearchField>
        {list}
      </div>
    </Autocomplete>
  );
}
